import React from 'react';

import { StepIndicator } from '@/components';
import { Button } from '@/design-system/button';

import { DisabledReason } from './dialogs';

/**
 * Step chrome shared by the WMS forms that carry many fields (outbound
 * request, purchase order, goods receipt). Forward moves validate the current
 * step locally; the server stays the source of truth and its 422 sends the
 * user back to the earliest step that has an error.
 */

export interface WizardStep {
  /** 1-based, matches `current`. */
  id: number;
  label: string;
}

/** Full stepper from `sm` up, the compact one-liner below (CLAUDE.md §4). */
export const WizardSteps: React.FC<{
  steps: WizardStep[];
  current: number;
  className?: string;
}> = ({ steps, current, className }) => (
  <div className={className} data-testid="wizard-steps">
    <StepIndicator
      steps={steps}
      currentStep={current}
      className="hidden sm:flex"
    />
    <StepIndicator
      steps={steps}
      currentStep={current}
      compact
      className="sm:hidden"
    />
  </div>
);

interface WizardFooterProps {
  step: number;
  total: number;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitIcon?: string;
  submitTestId?: string;
  busy?: boolean;
  /** Left-aligned cancel; omit on pages that have their own back link. */
  onCancel?: () => void;
  nextDisabled?: boolean;
  /** Shown next to Lanjut while it is off. */
  nextDisabledReason?: string | null;
}

/** Batal · Kembali · Lanjut — or the submit action on the last step. */
export const WizardFooter: React.FC<WizardFooterProps> = ({
  step,
  total,
  onBack,
  onNext,
  onSubmit,
  submitLabel,
  submitIcon = 'ri-check-line',
  submitTestId,
  busy,
  onCancel,
  nextDisabled,
  nextDisabledReason,
}) => {
  const last = step >= total;
  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        {onCancel && (
          <Button
            tone="neutral"
            style="ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Batal
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!last && nextDisabled && nextDisabledReason && (
          <DisabledReason testId="wizard-next-reason">
            {nextDisabledReason}
          </DisabledReason>
        )}
        {step > 1 && (
          <Button
            tone="neutral"
            style="stroke"
            onClick={onBack}
            disabled={busy}
            leftIcon={<i className="ri-arrow-left-line text-[14px]" />}
            data-testid="wizard-back"
          >
            Kembali
          </Button>
        )}
        {last ? (
          <Button
            onClick={onSubmit}
            loading={busy}
            leftIcon={<i className={`${submitIcon} text-[14px]`} />}
            data-testid={submitTestId}
          >
            {submitLabel}
          </Button>
        ) : (
          <Button
            onClick={onNext}
            disabled={nextDisabled}
            rightIcon={<i className="ri-arrow-right-line text-[14px]" />}
            data-testid="wizard-next"
          >
            Lanjut
          </Button>
        )}
      </div>
    </div>
  );
};
